import React, { useEffect, useState } from 'react'
import { Text } from 'blaze-ui/Text/Text'
import { Input } from 'almanac-ui/TextInputField/Input'
import { ChevronDown, ChevronUp } from 'blaze-ui/icons'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import Styles from './BrandKitForm.module.scss'
import { Button } from 'blaze-ui/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useIsUrlCrawlable } from 'hooks/useIsUrlCrawlable'
import { useImages } from 'components/Wizards/SelectImages/ImagesProvider'
import { BrandKit, Website } from 'types'
import { last } from 'lodash'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryClient } from 'react-query'
import { useCurrentImageLibrary } from 'hooks/useCurrentImageLibrary'
import { Images } from './Images/Images'
import cx from 'classnames'
import { useCreateSourceSync } from 'hooks/queries/workspaces/useCreateSourceSync'
import { useCreateBrandKitSource } from 'hooks/queries/useCreateBrandKitSource'
import { Processing } from './Processing'
import { WebsiteInput } from './WebsiteInput/WebsiteInput'
import { normalizeUrl } from './utils'
import { ConfirmBackModal } from '../BrandKitColorsAndFonts/ConfirmBackModal/ConfirmBackModal'
import { useConfirmModal } from 'hooks/useConfirmModal'
import { useDeleteBrandKit } from 'hooks/queries/brandKits/useDeleteBrandKit'

interface Props {
  onBack?: () => void
  onSkip?: () => void
  onNext: () => void
  title?: string
  brandKit: BrandKit
  withinOnboarding?: boolean
  onSubmit?: (value: boolean) => void
}

export interface NewWebsite {
  index: number
  url: string
  favicon?: string
}

export const BrandKitMultiplePagesForm: React.FC<Props> = ({
  onBack,
  onSkip,
  title,
  brandKit,
  withinOnboarding = true,
  onNext,
  onSubmit,
}) => {
  const [newUrl, setNewUrl] = useState<string>('')
  const [websites, setWebsites] = useState<NewWebsite[]>([])
  const [selectedUrls, setSelectedUrls] = useState<string[]>([])
  const workspaceId = useCurrentWorkspaceId()
  const imageLibrary = useCurrentImageLibrary()
  const queryClient = useQueryClient()
  const { mutate: deleteBrandKit } = useDeleteBrandKit({ id: brandKit.id })
  const backConfirm = useConfirmModal(ConfirmBackModal)

  const [isDirty, setIsDirty] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [showAllVisitedPages, setShowAllVisitedPages] = useState(false)
  const { topic, generateImageKeywords } = useImages()
  const { isValid, errorMessage } = useIsUrlCrawlable(newUrl.trim())

  const { mutateAsync: createSourceAsync, isLoading: isCreatingSource } = useCreateSourceSync()
  const { mutateAsync: createBrandKitSourceAsync, isLoading: isCreatingBrandKitSource } = useCreateBrandKitSource()
  const { uploadImageFiles, isUploading } = useImportMediaFiles(false, () => {
    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', brandKit.id, 'mediaFiles'])
    if (imageLibrary?.key) {
      queryClient.invalidateQueries(['workspace', workspaceId, 'files', imageLibrary.key])
    }
  })

  const isSubmitting = isCreatingSource || isCreatingBrandKitSource || isUploading
  const sourceMaterial = last(last(brandKit?.websites)?.source_materials)
  const text = sourceMaterial?.text
  const visitedWebsites = brandKit?.websites
  const visitedWebsitesToShow = showAllVisitedPages ? visitedWebsites : visitedWebsites.slice(0, 4)

  useEffect(() => {
    if (!topic && text) {
      generateImageKeywords({ topic: text })
    }
  }, [topic, text, generateImageKeywords])

  const addImagesToBrandKit = async () => {
    if (!imageLibrary?.key) return

    const files = await Promise.all(
      selectedUrls.map(async (url) => {
        const response = await fetch(url)
        const blob = await response.blob()
        return new File([blob], url, { type: blob.type })
      }),
    )
    return uploadImageFiles(files, imageLibrary.key, { brand_kit_id: brandKit.id }, false)
  }

  const handleBack = async () => {
    const confirm = await backConfirm()

    if (confirm) {
      deleteBrandKit()
      onBack?.()
    }
  }

  const handleSubmit = async () => {
    setIsProcessing(true)
    onSubmit?.(true)

    const createBrandKitSourcePromises = websites.map(async (website) => {
      return new Promise(async (resolve, reject) => {
        try {
          const data = await createSourceAsync({
            type: 'Website',
            url: normalizeUrl(website.url),
          })
          const source = data.source as Website

          const brandKitSource = await createBrandKitSourceAsync({
            brandKitId: brandKit.id,
            source: {
              id: source.id,
              type: source.type,
            },
            name: source.url,
            emoji: '',
          })
          resolve(brandKitSource)
        } catch (error) {
          reject(error)
        }
      })
    })

    await Promise.allSettled([...createBrandKitSourcePromises, addImagesToBrandKit()])
    onNext()
    onSubmit?.(false)
    setIsProcessing(false)
  }

  const onToggleImage = (url: string) => {
    setSelectedUrls((prev) => (prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]))
  }

  const handleAddWebsite = () => {
    setWebsites([...websites, { index: websites.length, url: newUrl }])
    setNewUrl('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') {
      setIsDirty(false)
      return
    }

    if (!isValid || !newUrl || websites.some((w) => w.url === newUrl)) return
    handleAddWebsite()
  }

  const onRemoveWebsite = (index: number) => {
    setWebsites(websites.filter((w) => w.index !== index))
  }

  if (isSubmitting || isProcessing) {
    return <Processing showCancel={false} websiteUrl={websites[0]?.url} cancelOnError={false} />
  }

  return (
    <MultiStepStepLayout
      title={title}
      onBack={handleBack}
      onSkip={onSkip}
      onNext={handleSubmit}
      isNextButtonDisabled={isSubmitting}
    >
      <div className={Styles.container}>
        <div className={Styles.websiteBox}>
          <Heading level={withinOnboarding ? 4 : 5}>Add more pages from your website</Heading>
          <div
            className={cx(Styles.websiteInputContainer, {
              [Styles.hasVisitedPages]: visitedWebsites.length > 0,
              [Styles.withinOnboarding]: withinOnboarding,
            })}
          >
            <div className={Styles.visitedPagesContainer}>
              <Text variant="label" color="var(--dark-80)">
                We already analyzed these pages:
              </Text>
              <div className={Styles.visitedPages}>
                {visitedWebsitesToShow.map((website: Website) => (
                  <div className={Styles.websitePill} key={website.url}>
                    <img src={website.favicon || visitedWebsitesToShow[0].favicon} alt={website.url} />
                    <Text variant="metadata" color="var(--dark-60)" lineClamp={1}>
                      {website.url}
                    </Text>
                  </div>
                ))}
              </div>
              {visitedWebsites.length > 4 && (
                <div className={Styles.showAllVisitedPagesButton}>
                  <Button
                    variant="editor"
                    frontIcon={showAllVisitedPages ? ChevronUp : ChevronDown}
                    size="xs"
                    onPress={() => setShowAllVisitedPages(!showAllVisitedPages)}
                  >
                    {showAllVisitedPages ? 'See Less' : `See ${visitedWebsites.length - 4} More`}
                  </Button>
                </div>
              )}
            </div>
            <div className={Styles.websiteSection}>
              <Input
                id="new-url"
                name="new-url"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                errorMessage={isDirty && !isValid ? errorMessage : undefined}
                placeholder="Enter other URL from your business website..."
                size="xl"
                className={Styles.newWebsiteField}
                onKeyDown={handleKeyDown}
                autoFocus
                autoCorrect="off"
              />
              <Button
                variant="ghost-tertiary"
                size="xl"
                onPress={handleAddWebsite}
                isDisabled={!isValid || !newUrl || websites.some((w) => w.url === newUrl)}
                aria-label="Add"
              >
                Add
              </Button>
            </div>
            <Text variant="metadata" color="var(--dark-60)">
              Add more URLs like www.website.com/about
            </Text>
            {websites.length > 0 && (
              <div className={Styles.moreWebsitesContainer}>
                {websites.map((website, index) => (
                  <WebsiteInput
                    key={index}
                    website={website}
                    index={index}
                    onRemoveWebsite={onRemoveWebsite}
                    setWebsites={(newUrl) =>
                      setWebsites(websites.map((w) => (w.index === website.index ? { ...w, url: newUrl } : w)))
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <Images selectedUrls={selectedUrls} onToggleImage={onToggleImage} brandKitId={brandKit.id} />
      </div>
    </MultiStepStepLayout>
  )
}
